import type { CSSProperties, ReactNode } from "react";

type Variant = "primary" | "coral" | "ghost" | "soft" | "danger";
type Size = "sm" | "md" | "lg";

interface Props {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  fullWidth?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  "data-testid"?: string;
}

const VARIANTS: Record<Variant, { bg: string; fg: string; border: string }> = {
  primary: { bg: "var(--forest)", fg: "#fff", border: "transparent" },
  coral: { bg: "var(--coral)", fg: "#fff", border: "transparent" },
  ghost: { bg: "transparent", fg: "var(--ink)", border: "var(--line-2)" },
  soft: { bg: "var(--bg-2)", fg: "var(--ink)", border: "transparent" },
  danger: { bg: "var(--bad)", fg: "#fff", border: "transparent" },
};

const SIZES: Record<Size, CSSProperties> = {
  sm: { padding: "8px 14px", fontSize: 13, height: 34 },
  md: { padding: "10px 18px", fontSize: 14, height: 44 },
  lg: { padding: "14px 24px", fontSize: 16, height: 56 },
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  fullWidth,
  style,
  onClick,
  disabled,
  type = "button",
  "data-testid": testId,
}: Props) {
  const v = VARIANTS[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        background: v.bg,
        color: v.fg,
        border: `1px solid ${v.border}`,
        borderRadius: 999,
        fontWeight: 600,
        letterSpacing: -0.1,
        width: fullWidth ? "100%" : undefined,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        ...SIZES[size],
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
